// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 86b5945f-9c56-462d-aecc-e86c141e5974

// ---------------------------------------------------------------------------
// Seed data — REPO-SAFE. Contains only:
//   - the base app CATALOG (Optimum-owned canonical definitions, no client data)
//   - tenant definitions (names only)
//   - a couple of fully SYNTHETIC demo fact sheets to exercise the UI
//
// NO real client instance data lives in source control. Real DFCI/NGHS data is
// loaded into DynamoDB locally (backend/imported.local.json, gitignored) and the
// app hydrates it from the API — it never ships in the repo.
// ---------------------------------------------------------------------------

// Tenants = customers. `logo` white-labels the portal; `status` active|archived.
// Logos use unguessable asset keys so the CDN cannot be used to enumerate the
// client list. The image lives in S3/CloudFront; the tenant row holds the link.
export const TENANTS = [
  { id: 'optimum', name: 'Optimum Healthcare IT', kind: 'master', color: '#0D5B9A', logo: '/tenant-assets/t-1c07fe5aa338.png', status: 'active' },
  { id: 'nghs', name: 'Northeast Georgia Health System', kind: 'client', color: '#4A5D23', logo: '/tenant-assets/t-9f3ac21b7e40.png', status: 'active' },
  { id: 'dfci', name: 'Dana-Farber Cancer Institute', kind: 'client', color: '#005A9C', logo: '/tenant-assets/t-4d81be0c95af.png', status: 'active' },
  // kind: 'sample' — synthetic tenants that exist to exercise the UI. They must
  // NEVER count toward the cross-client benchmark cohort: a "3-client minimum"
  // satisfied by two real clients plus fabricated data is not anonymity, because
  // the reader can work backwards to an individual client. Everything else in the
  // app treats these exactly like a client tenant.
  { id: 'stvincent', name: "St. Vincent's Health (demo)", kind: 'sample', color: '#6A1B9A', logo: '', status: 'active' },
  { id: 'demo', name: 'Demo Health System (sample)', kind: 'sample', color: '#00838F', logo: '', status: 'active' },
];

const tag = (group, value) => ({ group, value });

// ---------------------------------------------------------------------------
// MASTER CATALOG — base/reusable app definitions (no client crossover info).
// Safe to keep in the repo; this is Optimum's IP, built once, reused per client.
// ---------------------------------------------------------------------------
export const CATALOG = [
  { id: 'cat-absencesoft', name: 'AbsenceSoft', vendor: 'AbsenceSoft', category: 'HR / Leave Management', typicalHosting: 'SaaS', defaultTime: 'Tolerate', default6R: 'Repurchase',
    description: 'Cloud-based leave of absence and accommodation management (FMLA/ADA/PWFA eligibility, case management).',
    standardQuestions: ['SaaS available in AWS?', 'SSO providers supported?', 'PHI stored?', 'Data residency requirements?'] },
  { id: 'cat-epic', name: 'Epic Hyperspace', vendor: 'Epic Systems', category: 'EHR', typicalHosting: 'On Premise', defaultTime: 'Invest', default6R: 'Retain',
    description: 'Core electronic health record platform.',
    standardQuestions: ['Hosting model (on-prem/ECI/cloud)?', 'Interconnect interfaces?', 'DR tier?'] },
  { id: 'cat-adobe', name: 'Adobe PDF Pro', vendor: 'Adobe', category: 'Productivity', typicalHosting: 'SaaS', defaultTime: 'Tolerate', default6R: 'Retain',
    description: 'Professional PDF creation and editing.', standardQuestions: ['Licensing model?', 'SSO?'] },
  { id: 'cat-kronos', name: 'Kronos', vendor: 'UKG', category: 'Workforce Management', typicalHosting: 'On Premise', defaultTime: 'Migrate', default6R: 'Replatform',
    description: 'Time, attendance, and workforce management.', standardQuestions: ['SaaS migration path?', 'Integrations to payroll?'] },
  { id: 'cat-aflac', name: 'AFLAC', vendor: 'Aflac', category: 'HR / Benefits', typicalHosting: 'SaaS', defaultTime: 'Tolerate', default6R: 'Repurchase',
    description: 'Supplemental insurance / HR benefits portal.', standardQuestions: ['SSO?', 'Data feeds?'] },
];

// ---------------------------------------------------------------------------
// Synthetic demo fact sheets (made-up — safe for the repo). Exercise the full
// UI: sections, TIME/6R, integration flow with ports, migration knowledge.
// ---------------------------------------------------------------------------
export const SEED_FACT_SHEETS = [
  {
    id: 'demo-app-1', type: 'Application', tenantId: 'demo', name: 'Meridian Clinical Portal',
    fields: {
      name: 'Meridian Clinical Portal',
      description: 'Sample clinical web portal used to demonstrate Primordia. Entirely fictitious.',
      appPurpose: 'Front-door portal for scheduling, results review, and messaging (demo data).',
      lifecycle: '2023-01-01', lifecycleState: 'Active',
      applicationType: 'Clinical', drTier: 'Tier 2', hostingType: 'Hybrid',
      locationType: 'On-Prem IaaS', targetHosting: 'Cloud - IaaS',
      applicationOwner: 'Sample Owner', technicalOwner: 'Sample Engineer', vendorName: 'Meridian Systems (demo)',
      businessUnit: 'Clinical Operations',
      businessCriticality: 'Business Operational', functionalFit: 'Appropriate', technicalFit: 'Adequate',
      timeClassification: 'Invest', sixRClassification: 'Replatform',
      ssoAvailability: 'Available', ssoProviders: 'Entra ID',
      rto: '8 to 24 Hours', rpo: '4 to 8 hours', phi: true,
      lessonsLearned: 'Demo: latency-sensitive; validate interface engine throughput before cutover.',
      mustWhitelist: 'Outbound 443 to portal endpoints; HL7 to interface engine on TCP 6661 (demo values).',
      awsGotchas: 'Demo: confirm DNS + firewall parity; no hardcoded IPs.',
    },
    tags: [tag('Cell', 'Clinical Apps'), tag('DR Tier', 'DR Tier: 2'), tag('Managed By', 'DEMO CLINICAL')],
    quality: 'Approved',
    integrations: [
      { to: 'Sample EHR', via: 'Interface Engine (demo)', protocol: 'HL7 v2 · ADT/ORU', port: 'TCP 6661', direction: 'bidirectional', dataType: 'ADT / Results' },
      { to: 'Identity Provider', via: '', protocol: 'SAML', port: 'TCP 443', direction: 'outbound', dataType: 'SSO' },
    ],
    relations: {},
  },
  {
    id: 'demo-app-2', type: 'Application', tenantId: 'demo', name: 'Globex Scheduling',
    fields: {
      name: 'Globex Scheduling', description: 'Sample scheduling system (fictitious demo data).',
      appPurpose: 'Enterprise scheduling (demo).', lifecycleState: 'Active',
      applicationType: 'Business', drTier: 'Tier 3', hostingType: 'SaaS', locationType: 'SaaS',
      applicationOwner: 'Sample Owner', vendorName: 'Globex (demo)', businessUnit: 'Business Operations',
      businessCriticality: 'Administrative Service', functionalFit: 'Appropriate', technicalFit: 'Adequate',
      timeClassification: 'Tolerate', sixRClassification: 'Repurchase',
    },
    tags: [tag('Cell', 'Business Apps'), tag('DR Tier', 'DR Tier: 3'), tag('Managed By', 'DEMO OPS')],
    quality: 'Draft',
    integrations: [
      { to: 'Sample EHR', via: 'Interface Engine (demo)', protocol: 'HL7 v2 · SIU', port: 'TCP 6662', direction: 'outbound', dataType: 'Scheduling (SIU)' },
    ],
    relations: {},
  },
];
